import { displayFieldName } from '@eis/patterns/reporting';
import { ALIASES, type DatasetDocument } from '../../datasets/model/datasets';

/** Adds the next source (b, c, d — at most four, S-1), LEFT-joined to the base on a common field. */
export function addSource(doc: DatasetDocument, view: string, on: string): DatasetDocument {
  const alias = ALIASES[doc.sources.length];
  if (alias === undefined) return doc;
  return {
    ...doc,
    sources: [...doc.sources, { alias, view }],
    joins: [
      ...(doc.joins ?? []),
      { left: 'a', right: alias, type: 'LEFT', on: [{ leftField: on, rightField: on }] },
    ],
  };
}

/** Removes a source with its join and its fields (never the base). */
export function removeSource(doc: DatasetDocument, alias: string): DatasetDocument {
  if (alias === 'a') return doc;
  return {
    ...doc,
    sources: doc.sources.filter((s) => s.alias !== alias),
    joins: (doc.joins ?? []).filter((j) => j.right !== alias && j.left !== alias),
    fields: doc.fields.filter((f) => f.source !== alias),
  };
}

/** Includes or drops a source field; a new one gets a unique alias (S-1). */
export function toggleField(
  doc: DatasetDocument,
  source: string,
  field: string,
  include: boolean,
): DatasetDocument {
  if (!include)
    return {
      ...doc,
      fields: doc.fields.filter((f) => !(f.source === source && f.field === field)),
    };
  if (doc.fields.some((f) => f.source === source && f.field === field)) return doc;
  const taken = new Set(doc.fields.map((f) => f.alias));
  let alias = displayFieldName(field);
  if (taken.has(alias)) alias = `${alias} (${source.toUpperCase()})`;
  return { ...doc, fields: [...doc.fields, { source, field, alias }] };
}

export function setAlias(
  doc: DatasetDocument,
  source: string,
  field: string,
  alias: string,
): DatasetDocument {
  return {
    ...doc,
    fields: doc.fields.map((f) => (f.source === source && f.field === field ? { ...f, alias } : f)),
  };
}
